import React, { MutableRefObject, useCallback, useEffect, useLayoutEffect, useRef } from 'react'
import classNames from 'classnames'

import { dispatchCloseModalEvent, dispatchOpenModalEvent } from '../../../utils/cookie.utils'
import { useOrientationChange } from './ExpandableModal/hooks/useOrientationChange'
import { ModalProps } from './types'

import styles from './ModalDesktop.module.scss'

const useScrollableCheck = (
  props: ModalProps & { isLoading?: boolean },
  ref: MutableRefObject<HTMLDivElement | null>
) => {
  const { isLoading } = props

  useLayoutEffect(() => {
    const el = ref.current
    if (el) {
      const { canScroll } = checkScroll()

      if (canScroll) {
        document.documentElement.classList.add('is-modal-scrollable')
      }

      return () => {
        document.documentElement.classList.remove('is-modal-scrollable')
      }
    }
  }, [isLoading])

  useEffect(() => {
    checkScroll()
  }, [])

  const checkScroll = () => {
    const el = ref.current
    if (el) {
      const scrollNumber = el?.scrollTop || 0
      const clientHeight = el?.clientHeight || 0
      const scrollHeight = el?.scrollHeight || 0

      const canScroll = scrollNumber > 0 || scrollNumber + clientHeight < scrollHeight

      return { canScroll }
    }
    return { canScroll: undefined }
  }

  useOrientationChange(checkScroll, props)
}

export const useModalEffect = (props: ModalProps): void => {
  const { onClose } = props

  useLayoutEffect(() => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur()
    }

    document.documentElement.classList.add('is-clipped', 'is-modal-open')
    dispatchOpenModalEvent()

    return () => {
      dispatchCloseModalEvent()
      document.documentElement.classList.remove('is-clipped', 'is-modal-open')
    }
  }, [])

  const handleKeyUp = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    },
    [onClose]
  )

  useEffect(() => {
    window.addEventListener('keyup', handleKeyUp)

    return () => {
      window.removeEventListener('keyup', handleKeyUp)
    }
  }, [handleKeyUp])
}

interface ModalHandlers {
  handleBackgroundMouseDown: (event: React.MouseEvent) => void
  handleContentClick: (event: React.MouseEvent) => void
}

export const useModalHandlers = (props: ModalProps): ModalHandlers => {
  const { onClose } = props

  const handleBackgroundMouseDown = (event: React.MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
    onClose()
  }

  const handleContentClick = (event: React.MouseEvent) => {
    event.stopPropagation()
  }

  return { handleBackgroundMouseDown, handleContentClick }
}

export const ModalDesktop: React.FC<ModalProps & { isLoading?: boolean }> = (props) => {
  const { children, size } = props

  const scrollContainerRef = useRef(null)

  useModalEffect(props)
  useScrollableCheck(props, scrollContainerRef)
  const { handleContentClick, handleBackgroundMouseDown } = useModalHandlers(props)

  return (
    <div
      id='modal'
      className={classNames(styles.modalContainer, {
        [`is-${size}`]: size,
      })}
    >
      <div
        className={styles.modalOverlay}
        onClick={handleBackgroundMouseDown}
        onMouseDown={handleBackgroundMouseDown}
      />
      <div
        onMouseDown={handleBackgroundMouseDown}
        className={classNames('modal', 'is-active', styles.modal)}
      >
        <div
          ref={scrollContainerRef}
          onMouseDown={handleContentClick}
          className={classNames('modal-card', styles.modalCard)}
        >
          <div className={styles.modalWrapper} onMouseDown={handleContentClick}>
            <div>{children}</div>
          </div>
        </div>
      </div>
    </div>
  )
}
